import axios from "axios";
import config from "../../config.json";
import {
  getAuthToken,
  isFranchisePortalAuthSession,
  isRestaurantImpersonationAuthSession,
} from "./authToken";
import { forceLogoutToLogin } from "./sessionAuth";

const DEVICE_KEY = "adminDeviceId";
const ACTIVE_KEY = "adminLastActiveAt";

function getDeviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

export function getAdminDeviceId() {
  return getDeviceId();
}

export function markAdminSessionOpen() {
  localStorage.setItem(ACTIVE_KEY, String(Date.now()));
}

function shouldWatch() {
  if (!getAuthToken()) return false;
  if (isFranchisePortalAuthSession() || isRestaurantImpersonationAuthSession()) {
    return false;
  }
  const path = window.location.pathname || "";
  if (
    path === "/login" ||
    path.startsWith("/reset-password") ||
    path.startsWith("/page-forgot-password") ||
    path.startsWith("/page-register")
  ) {
    return false;
  }
  return true;
}

export function startAdminInactivityWatch() {
  let hours = 3;
  let hoursLoaded = false;
  let stopped = false;

  const idleLimit = () => (hours > 0 ? hours * 60 * 60 * 1000 : 0);

  const tooIdle = () => {
    const limit = idleLimit();
    if (!limit) return false;
    const last = Number(localStorage.getItem(ACTIVE_KEY) || 0);
    if (!last) return false;
    return Date.now() - last >= limit;
  };

  const markOpen = () => {
    localStorage.setItem(ACTIVE_KEY, String(Date.now()));
  };

  const logout = () => {
    if (stopped) return;
    stopped = true;
    localStorage.removeItem(ACTIVE_KEY);
    forceLogoutToLogin();
  };

  const ping = async () => {
    if (stopped || !shouldWatch()) return;
    try {
      const response = await axios.post(
        `${config.dev}/auth/admin-activity`,
        {},
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            "x-admin-device": getDeviceId(),
          },
        }
      );
      const next = Number(response.data?.hours);
      if (Number.isFinite(next) && next >= 0) hours = next;
      hoursLoaded = true;
      if (hours === 0) {
        localStorage.removeItem(ACTIVE_KEY);
        return;
      }
      if (tooIdle()) {
        logout();
        return;
      }
      if (document.visibilityState === "visible") markOpen();
    } catch (error) {
      if (error?.response?.data?.code === "IDLE_TIMEOUT") logout();
    }
  };

  const tick = () => {
    if (stopped || !shouldWatch()) return;
    if (!hoursLoaded) {
      ping();
      return;
    }
    if (hours === 0) return;
    if (tooIdle()) {
      logout();
      return;
    }
    if (document.visibilityState === "visible") ping();
  };

  document.addEventListener("visibilitychange", tick);
  const interval = window.setInterval(tick, 60 * 1000);
  tick();

  return () => {
    stopped = true;
    window.clearInterval(interval);
    document.removeEventListener("visibilitychange", tick);
  };
}
